import { type JSX, type ReactNode, useState } from 'react';
import { Accordion, Box, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';

/**
 * Primitivas de layout do painel.
 *
 * Existem para resolver um problema de leitura, não de estética: antes, todo
 * bloco tinha o mesmo peso visual, os filtros ocupavam a área nobre e nada na
 * tela dizia por que aquele bloco estava ali. Três peças resolvem isso:
 *
 *  - `Secao`: número, título e SUBTÍTULO. O subtítulo é obrigatório porque é
 *    ele que diz para que serve o bloco — "o que a secretaria faz com isto".
 *  - `Vazio`: estado vazio com INSTRUÇÃO. A base tem poucas dezenas de
 *    atendimentos e vai viver vazia por semanas; "nada aqui" seria a tela
 *    normal do painel, e uma tela normal que não ensina nada é desperdício.
 *  - `Numero`: a faixa de resumo, com os números-chave antes de qualquer
 *    gráfico.
 *
 * PALETA CONTIDA. Nenhuma delas pinta nada por padrão: cinza e preto no
 * grosso, cor só onde ela carrega significado — e sempre com texto ao lado,
 * nunca cor sozinha como sinal.
 */

/**
 * Só o cabeçalho numerado.
 *
 * Existe para telas cujos blocos já estão montados em sequência plana (a de
 * métricas é assim): dá a mesma hierarquia sem obrigar a envolver o conteúdo
 * de cada bloco, que é onde um refactor de JSX quebra o que funcionava.
 */
export function CabecalhoDeSecao({
  numero,
  titulo,
  subtitulo,
  acao,
}: {
  numero: string;
  titulo: string;
  subtitulo: string;
  acao?: ReactNode;
}): JSX.Element {
  return (
    <Group justify="space-between" align="flex-end" wrap="nowrap" mt="lg" mb="sm" gap="sm">
      <Group gap="sm" align="baseline" wrap="nowrap">
        {/* O número é a âncora visual da hierarquia: discreto, monoespaçado
            e do mesmo tamanho em todas as seções. */}
        <Text ff="monospace" fz={28} fw={300} c="dimmed" lh={1}>
          {numero}
        </Text>
        <div>
          <Text fz="lg" fw={600} lh={1.2}>
            {titulo}
          </Text>
          <Text size="sm" c="dimmed">
            {subtitulo}
          </Text>
        </div>
      </Group>
      {acao}
    </Group>
  );
}

export function Secao({
  numero,
  titulo,
  subtitulo,
  acao,
  children,
}: {
  /** `'01'`. String, não número: o zero à esquerda é parte do rótulo. */
  numero: string;
  titulo: string;
  /** Para que serve o bloco. Obrigatório — ver nota acima. */
  subtitulo: string;
  /** Filtro ou botão do bloco, à direita do título. */
  acao?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Box component="section" mb="xl">
      <CabecalhoDeSecao numero={numero} titulo={titulo} subtitulo={subtitulo} acao={acao} />
      {children}
    </Box>
  );
}

/**
 * Estado vazio que ensina.
 *
 * `titulo` diz o que não há; `children` diz o que fazer para haver. Vazio
 * apologético ("nada por aqui :(") gasta a mesma área e não move ninguém.
 */
export function Vazio({
  titulo,
  children,
}: {
  titulo: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Paper withBorder p="lg" radius="md">
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          {titulo}
        </Text>
        {children && (
          <Text size="sm" c="dimmed">
            {children}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * Gráfico sem dado suficiente.
 *
 * Diferente de `Vazio`: aqui EXISTE dado, só não o bastante para a forma do
 * gráfico dizer a verdade. Desenhar uma linha com três pontos é pior que não
 * desenhar — a linha sugere tendência onde há ruído.
 */
export function SemDadosSuficientes({
  titulo,
  children,
}: {
  titulo: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Paper withBorder p="lg" radius="md" style={{ borderStyle: 'dashed' }}>
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          {titulo}
        </Text>
        {children && (
          <Text size="sm" c="dimmed">
            {children}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * Barra de filtros, fora da área nobre.
 *
 * Antes os filtros abriam a tela: três caixas de seleção em tamanho cheio
 * antes de qualquer conteúdo, como se escolher o recorte fosse a tarefa. Aqui
 * eles ficam numa faixa discreta DEPOIS do título — a tarefa é ler a lista; o
 * filtro é ajuste.
 */
export function BarraDeFiltros({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Paper withBorder p="xs" radius="md" mb="sm" bg="var(--mantine-color-default)">
      <Group gap="sm" align="flex-end" wrap="wrap">
        {children}
      </Group>
    </Paper>
  );
}

/** Um número da faixa de resumo. */
export function Numero({
  rotulo,
  valor,
  nota,
  cor,
  aoClicar,
}: {
  rotulo: string;
  /** Já formatado. `'—'` para "não se aplica" — nunca 0 fingindo de leitura. */
  valor: string | number;
  nota?: string | null;
  /** Cor do Mantine. Use só quando o número em si carrega urgência. */
  cor?: string;
  aoClicar?: () => void;
}): JSX.Element {
  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      {...(aoClicar
        ? {
            component: 'button' as const,
            type: 'button' as const,
            onClick: aoClicar,
            style: { cursor: 'pointer', textAlign: 'left' as const },
          }
        : {})}
    >
      <Stack gap={2}>
        <Text fz={30} fw={600} lh={1} c={cor}>
          {valor}
        </Text>
        <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.04em' }}>
          {rotulo}
        </Text>
        {nota && (
          <Text size="xs" c="dimmed">
            {nota}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

/** A faixa de resumo. Quatro colunas em tela larga, duas no celular. */
export function FaixaDeNumeros({ children }: { children: ReactNode }): JSX.Element {
  return (
    <SimpleGrid cols={{ base: 2, sm: 2, md: 4 }} spacing="sm">
      {children}
    </SimpleGrid>
  );
}

/** Porcentagem com uma casa, ou `—` quando não há base para a conta. */
export function porcento(fracao: number | null): string {
  return fracao === null ? '—' : `${(fracao * 100).toFixed(1)}%`;
}

/**
 * Bloco de ajuda da tela.
 *
 * UM idioma só de ajuda no painel: sempre `Accordion variant="contained"`,
 * sempre fechado ao abrir a tela, sempre no mesmo lugar relativo (depois do
 * cabeçalho e dos filtros, antes do conteúdo; nas telas de duas colunas, no pé
 * da coluna esquerda). Está aqui como componente, e não como convenção, para
 * que a próxima tela não precise acertar o padrão de memória.
 *
 * O que entra no corpo é só o que a tela NÃO mostra sozinha. Regra dura: se a
 * informação já está visível em algum lugar, aqui ela é REFERENCIADA — duas
 * cópias da mesma regra divergem na primeira vez que uma delas muda.
 *
 * O público é a secretaria, que não acompanhou a construção: frase curta,
 * linguagem de operação, nenhum nome de variável.
 */
export function BlocoDeAjuda({
  secoes,
  aoAlternar,
}: {
  /** Uma ou mais seções. Mais de uma só quando os assuntos são de fato dois. */
  secoes: { id: string; titulo: string; corpo: ReactNode }[];
  /**
   * Avisa que a ajuda abriu ou fechou.
   *
   * Existe para as telas de coluna GRUDADA: lá a ajuda aberta empurraria o pé
   * da coluna para fora da janela, e numa coluna grudada o que passa da janela
   * não volta com rolagem. Quem recebe isto encurta o que está acima. Ver o
   * `ScrollArea` da árvore, em `Menu.tsx`.
   */
  aoAlternar?: (aberto: boolean) => void;
}): JSX.Element {
  const [aberta, setAberta] = useState<string | null>(null);

  return (
    <Accordion
      variant="contained"
      value={aberta}
      onChange={(v) => {
        setAberta(v);
        aoAlternar?.(v !== null);
      }}
    >
      {secoes.map((s) => (
        <Accordion.Item key={s.id} value={s.id}>
          <Accordion.Control>
            <Text size="sm">{s.titulo}</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">{s.corpo}</Stack>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}
